/**
 * Tech Portal Settings Page
 * Professional settings management for tech portal users
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useTheme, type ThemePreference } from '../../context/ThemeContext';
import { useToast } from '../../components/shared/Toast';
import {
  MdSettings,
  MdSecurity,
  MdNotifications,
  MdKey,
  MdSave,
  MdPerson,
  MdLanguage,
  MdPalette,
  MdAccessTime,
  MdLock,
  MdEmail,
  MdPhone,
  MdInfo,
  MdCheckCircle,
  MdError,
  MdVisibility,
  MdVisibilityOff,
  MdScreenshotMonitor,
  MdVideocam,
  MdScreenShare,
  MdShield,
  MdWarning,
  MdDeleteForever,
  MdKeyboardArrowDown,
} from 'react-icons/md';
import { cn } from '../../lib/utils';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@euroasiann/shared';
import { PasswordRequirementsHint, PageSkeleton } from '@euroasiann/shared/ui';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface UserPreferences {
  timezone?: string;
  language?: string;
  theme?: 'light' | 'dark' | 'system';
  emailNotifications?: boolean;
  systemAlerts?: boolean;
  licenseExpiryAlerts?: boolean;
  userActivityAlerts?: boolean;
  onboardingAlerts?: boolean;
}

interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const CLEAN_DATA_CONFIRM_PHRASE = 'CLEAN ALL DATA';

interface CleanDataStats {
  organizations: number;
  users: number;
  roles: number;
  customerOnboardings: number;
  vendorOnboardings: number;
  invitationTokens: number;
  refreshTokens: number;
  deletedCollections?: Record<string, number>;
}

interface ContentProtectionSettings {
  screenshotAllowed: boolean;
  screenRecordingAllowed: boolean;
  screenShareAllowed: boolean;
}

export function SettingsPage() {
  const { user } = useAuth();
  const { setTheme } = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'account' | 'security' | 'notifications' | 'api' | 'content-protection' | 'data-management'>('account');

  // Account settings
  const [accountSettings, setAccountSettings] = useState({
    timezone: 'UTC',
    language: 'en',
    theme: 'system' as 'light' | 'dark' | 'system',
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    systemAlerts: true,
    licenseExpiryAlerts: true,
    userActivityAlerts: false,
    onboardingAlerts: true,
  });

  // Content protection settings (global policy — defaults all DISABLED)
  const [contentProtection, setContentProtection] = useState<ContentProtectionSettings>({
    screenshotAllowed: false,
    screenRecordingAllowed: false,
    screenShareAllowed: false,
  });

  // Password change
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [passwordSectionOpen, setPasswordSectionOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Clean data (customer/vendor wipe)
  const [cleanDataConfirm, setCleanDataConfirm] = useState('');
  const [showCleanDataModal, setShowCleanDataModal] = useState(false);
  const [cleanDataStats, setCleanDataStats] = useState<CleanDataStats | null>(null);

  // Load preferences from localStorage or fetch from API
  const loadPreferences = (): UserPreferences => {
    try {
      const stored = localStorage.getItem('user-preferences');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {}
    return {};
  };

  // Fetch user preferences (try API first, fallback to localStorage)
  const { data: preferences, isLoading: preferencesLoading } = useQuery<UserPreferences>({
    queryKey: ['user-preferences', user?._id],
    queryFn: async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/auth/preferences`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          const prefs = data.data || {};
          localStorage.setItem('user-preferences', JSON.stringify(prefs));
          return prefs;
        }
      } catch {}
      // Fallback to localStorage
      return loadPreferences();
    },
    enabled: !!user?._id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Initialize settings from preferences
  useEffect(() => {
    if (preferences) {
      setAccountSettings({
        timezone: preferences.timezone || 'UTC',
        language: preferences.language || 'en',
        theme: preferences.theme || 'system',
      });
      setNotificationSettings({
        emailNotifications: preferences.emailNotifications ?? true,
        systemAlerts: preferences.systemAlerts ?? true,
        licenseExpiryAlerts: preferences.licenseExpiryAlerts ?? true,
        userActivityAlerts: preferences.userActivityAlerts ?? false,
        onboardingAlerts: preferences.onboardingAlerts ?? true,
      });
    }
  }, [preferences]);

  const savePreferencesMutation = useMutation({
    mutationFn: async (prefs: UserPreferences) => {
      // Save to localStorage immediately
      localStorage.setItem('user-preferences', JSON.stringify(prefs));
      
      // Try to save to backend
      try {
        const response = await fetch(`${API_URL}/api/v1/auth/preferences`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify(prefs),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to save preferences' }));
          // Still return success since localStorage was saved
          console.warn('Backend save failed, but preferences saved locally:', error);
        }
        return { success: true, data: prefs };
      } catch (error) {
        // Still return success since localStorage was saved
        console.warn('Backend save failed, but preferences saved locally:', error);
        return { success: true, data: prefs };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences', user?._id] });
      toast.success('Preferences saved successfully!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save preferences: ${error.message}`);
    },
  });

  // Fetch content-protection settings
  const { data: cpData } = useQuery<ContentProtectionSettings>({
    queryKey: ['settings', 'content-protection'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/tech/settings/content-protection`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!res.ok) throw new Error('Failed to fetch content protection settings');
      const json = await res.json();
      return json.data?.data ?? { screenshotAllowed: false, screenRecordingAllowed: false, screenShareAllowed: false };
    },
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (cpData) setContentProtection(cpData);
  }, [cpData]);

  const saveContentProtectionMutation = useMutation({
    mutationFn: async (data: ContentProtectionSettings) => {
      const res = await fetch(`${API_URL}/api/v1/tech/settings/content-protection`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ data }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || 'Failed to save');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'content-protection'] });
      toast.success('Content protection policy saved! Users will see updated settings on next page load.');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const response = await fetch(`${API_URL}/api/v1/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to change password');
      }
      return response.json();
    },
    onSuccess: () => {
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      toast.success('Password changed successfully!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to change password: ${error.message}`);
    },
  });

  const cleanDataMutation = useMutation({
    mutationFn: async (confirmPhrase: string) => {
      const response = await fetch(`${API_URL}/api/v1/tech/clean-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ confirmPhrase }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.error || 'Failed to clean portal data');
      }
      return json.data as CleanDataStats;
    },
    onSuccess: (stats) => {
      setCleanDataStats(stats);
      setCleanDataConfirm('');
      setShowCleanDataModal(false);
      queryClient.invalidateQueries();
      toast.success('Customer and vendor portal data removed. Admin and tech logins preserved.');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSaveAccount = () => {
    const prefs: UserPreferences = {
      timezone: accountSettings.timezone,
      language: accountSettings.language,
      theme: accountSettings.theme,
    };
    savePreferencesMutation.mutate(prefs);
  };

  const handleApplyDisplaySettings = () => {
    const themePref: ThemePreference =
      accountSettings.theme === 'system' ? 'auto' : (accountSettings.theme as ThemePreference);
    setTheme(themePref);
    localStorage.setItem('techDisplaySettings', JSON.stringify({ theme: themePref }));
    toast.success('Display preferences applied');
  };

  const handleNotificationToggle = (key: keyof typeof notificationSettings, checked: boolean) => {
    const previous = notificationSettings;
    const next = { ...previous, [key]: checked };
    setNotificationSettings(next);
    const prefs: UserPreferences = {
      emailNotifications: next.emailNotifications,
      systemAlerts: next.systemAlerts,
      licenseExpiryAlerts: next.licenseExpiryAlerts,
      userActivityAlerts: next.userActivityAlerts,
      onboardingAlerts: next.onboardingAlerts,
    };
    localStorage.setItem('user-preferences', JSON.stringify({ ...loadPreferences(), ...prefs }));
    fetch(`${API_URL}/api/v1/auth/preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      body: JSON.stringify(prefs),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to save preferences' }));
          throw new Error(error.error || 'Failed to save notification settings');
        }
        queryClient.invalidateQueries({ queryKey: ['user-preferences', user?._id] });
      })
      .catch((error: Error) => {
        setNotificationSettings(previous);
        toast.error(error.message || 'Failed to save notification settings');
      });
  };

  const handleChangePassword = () => {
    setPasswordErrors({});

    // Validation
    if (!passwordData.currentPassword) {
      setPasswordErrors({ currentPassword: 'Current password is required' });
      return;
    }
    if (!passwordData.newPassword) {
      setPasswordErrors({ newPassword: 'New password is required' });
      return;
    }
    if (!isPasswordStrong(passwordData.newPassword)) {
      setPasswordErrors({ newPassword: PASSWORD_POLICY_MESSAGE });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  const tabs = [
    { id: 'account' as const, label: 'Account', icon: MdPerson },
    { id: 'security' as const, label: 'Security', icon: MdSecurity },
    { id: 'notifications' as const, label: 'Notifications', icon: MdNotifications },
    { id: 'api' as const, label: 'API & Integration', icon: MdKey },
    { id: 'content-protection' as const, label: 'Content Protection', icon: MdShield },
    { id: 'data-management' as const, label: 'Clean Data', icon: MdDeleteForever },
  ];

  if (preferencesLoading) {
    return (
      <div className="p-8">
        <PageSkeleton variant="page" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen p-8 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-[hsl(var(--foreground))] mb-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          Settings
        </h1>
        <p className="text-lg text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 font-medium">
          Manage your account preferences and system settings
        </p>
      </div>

      {/* Settings Container */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="p-4 rounded-xl border border-[#0088cc]/20 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-left',
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                      : 'text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/10 dark:hover:bg-slate-800'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="p-6 rounded-xl border border-[#0088cc]/20 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            {/* Account Settings */}
            {activeTab === 'account' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(var(--foreground))] dark:text-white mb-1">Account Settings</h2>
                  <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Manage your account preferences</p>
                </div>

                <div className="space-y-6">
                  {/* Timezone */}
                  <div>
                    <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2 flex items-center gap-2">
                      <MdAccessTime className="w-4 h-4" /> Timezone
                    </label>
                    <select
                      value={accountSettings.timezone}
                      onChange={(e) => setAccountSettings({ ...accountSettings, timezone: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-[#0088cc]/25 dark:border-slate-600 bg-white dark:bg-slate-800 text-[hsl(var(--foreground))] dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                      <option value="Europe/London">London (GMT)</option>
                      <option value="Europe/Paris">Paris (CET)</option>
                      <option value="Asia/Dubai">Dubai (GST)</option>
                      <option value="Asia/Singapore">Singapore (SGT)</option>
                      <option value="Asia/Kolkata">India (IST)</option>
                    </select>
                  </div>

                  {/* Language */}
                  <div>
                    <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2 flex items-center gap-2">
                      <MdLanguage className="w-4 h-4" /> Language
                    </label>
                    <select
                      value={accountSettings.language}
                      onChange={(e) => setAccountSettings({ ...accountSettings, language: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-[#0088cc]/25 dark:border-slate-600 bg-white dark:bg-slate-800 text-[hsl(var(--foreground))] dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="ar">Arabic</option>
                      <option value="hi">Hindi</option>
                    </select>
                  </div>

                  {/* Theme */}
                  <div>
                    <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2 flex items-center gap-2">
                      <MdPalette className="w-4 h-4" /> Theme
                    </label>
                    <select
                      value={accountSettings.theme}
                      onChange={(e) => setAccountSettings({ ...accountSettings, theme: e.target.value as any })}
                      className="w-full px-4 py-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[#0088cc] focus:border-[#0088cc] transition-all"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="system">Auto (System)</option>
                    </select>
                  </div>

                  <div className="flex flex-wrap gap-3 pt-4 border-t border-[hsl(var(--border))]">
                    <button
                      type="button"
                      onClick={handleApplyDisplaySettings}
                      className="px-6 py-3 bg-[#0088cc] hover:bg-[#0077b3] text-white font-semibold rounded-lg transition-colors"
                    >
                      Apply Display Preferences
                    </button>
                    <button
                      onClick={handleSaveAccount}
                      disabled={savePreferencesMutation.isPending}
                      className="px-6 py-3 border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-50 text-[hsl(var(--foreground))] font-semibold rounded-lg transition-colors flex items-center gap-2"
                    >
                      <MdSave className="w-5 h-5" />
                      {savePreferencesMutation.isPending ? 'Saving...' : 'Save Account Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(var(--foreground))] dark:text-white mb-1">Security Settings</h2>
                  <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Manage your password and security preferences</p>
                </div>

                <div className="space-y-6">
                  {/* Change Password */}
                  <div className="p-6 rounded-lg border border-[#0088cc]/20 dark:border-slate-700 bg-[#0088cc]/5 dark:bg-slate-800/50">
                    <button
                      type="button"
                      onClick={() => setPasswordSectionOpen((open) => !open)}
                      className="flex w-full items-center justify-between gap-3 text-left"
                      aria-expanded={passwordSectionOpen}
                    >
                      <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white flex items-center gap-2">
                        <MdLock className="w-5 h-5" /> Change Password
                      </h3>
                      <MdKeyboardArrowDown
                        className={`h-6 w-6 shrink-0 text-[#0088cc] transition-transform ${passwordSectionOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {passwordSectionOpen && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                          Current Password
                        </label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className={cn(
                              'w-full px-4 py-3 pr-10 rounded-lg border bg-white dark:bg-slate-800 text-[hsl(var(--foreground))] dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all',
                              passwordErrors.currentPassword
                                ? 'border-red-500 dark:border-red-500'
                                : 'border-[#0088cc]/25 dark:border-slate-600'
                            )}
                            placeholder="Enter current password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword((open) => !open)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                            aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                          >
                            {showCurrentPassword ? <MdVisibilityOff className="w-5 h-5" /> : <MdVisibility className="w-5 h-5" />}
                          </button>
                        </div>
                        {passwordErrors.currentPassword && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                            <MdError className="w-4 h-4" /> {passwordErrors.currentPassword}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className={cn(
                              'w-full px-4 py-3 pr-10 rounded-lg border bg-white dark:bg-slate-800 text-[hsl(var(--foreground))] dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all',
                              passwordErrors.newPassword
                                ? 'border-red-500 dark:border-red-500'
                                : 'border-[#0088cc]/25 dark:border-slate-600'
                            )}
                            placeholder="Min 8 chars, uppercase, number, symbol"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword((open) => !open)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                            aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                          >
                            {showNewPassword ? <MdVisibilityOff className="w-5 h-5" /> : <MdVisibility className="w-5 h-5" />}
                          </button>
                        </div>
                        <PasswordRequirementsHint password={passwordData.newPassword} />
                        {passwordErrors.newPassword && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                            <MdError className="w-4 h-4" /> {passwordErrors.newPassword}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className={cn(
                              'w-full px-4 py-3 pr-10 rounded-lg border bg-white dark:bg-slate-800 text-[hsl(var(--foreground))] dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all',
                              passwordErrors.confirmPassword
                                ? 'border-red-500 dark:border-red-500'
                                : 'border-[#0088cc]/25 dark:border-slate-600'
                            )}
                            placeholder="Confirm new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword((open) => !open)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                            aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                          >
                            {showConfirmPassword ? <MdVisibilityOff className="w-5 h-5" /> : <MdVisibility className="w-5 h-5" />}
                          </button>
                        </div>
                        {passwordErrors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                            <MdError className="w-4 h-4" /> {passwordErrors.confirmPassword}
                          </p>
                        )}
                      </div>
                      <div className="pt-2 flex justify-end">
                        <button
                          onClick={handleChangePassword}
                          disabled={
                            changePasswordMutation.isPending ||
                            !passwordData.currentPassword ||
                            !isPasswordStrong(passwordData.newPassword) ||
                            passwordData.newPassword !== passwordData.confirmPassword
                          }
                          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 text-white font-semibold rounded-lg shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all flex items-center gap-2"
                        >
                          <MdLock className="w-5 h-5" />
                          {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
                        </button>
                      </div>
                    </div>
                    )}
                  </div>

                  {/* Security Info */}
                  <div className="p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
                    <div className="flex items-start gap-3">
                      <MdInfo className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="text-sm text-blue-800 dark:text-blue-300">
                        <p className="font-semibold mb-1">Password Requirements:</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
                          <li>Minimum 8 characters</li>
                          <li>Use a combination of letters, numbers, and special characters</li>
                          <li>Do not reuse your last 3 passwords</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(var(--foreground))] dark:text-white mb-1">Notification Settings</h2>
                  <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Manage your notification preferences</p>
                </div>
                <div className="space-y-4">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive notifications via email' },
                    { key: 'systemAlerts', label: 'System Alerts', description: 'Important system-wide alerts and updates' },
                    { key: 'licenseExpiryAlerts', label: 'License Expiry Alerts', description: 'Get notified when licenses are about to expire' },
                    { key: 'userActivityAlerts', label: 'User Activity Alerts', description: 'Notifications about user activities and changes' },
                    { key: 'onboardingAlerts', label: 'Onboarding Alerts', description: 'Notifications about new onboarding submissions' },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex items-start justify-between p-4 rounded-lg border border-[#0088cc]/20 dark:border-slate-700 bg-[#0088cc]/5 dark:bg-slate-800/50"
                    >
                      <div className="flex-1">
                        <label className="text-sm font-semibold text-[hsl(var(--foreground))] dark:text-white block mb-1">
                          {item.label}
                        </label>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">{item.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={notificationSettings[item.key as keyof typeof notificationSettings]}
                          onChange={(e) =>
                            handleNotificationToggle(
                              item.key as keyof typeof notificationSettings,
                              e.target.checked
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#0088cc]/15 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-[#0088cc]/20 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-[#0088cc]/25 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content Protection */}
            {activeTab === 'content-protection' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(var(--foreground))] dark:text-white mb-1 flex items-center gap-2">
                    <MdShield className="w-7 h-7 text-red-500" /> Content Protection
                  </h2>
                  <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">
                    Global policy — controls what all users across all portals can capture. Changes take effect on next page load.
                  </p>
                </div>

                {/* Platform limitation notice */}
                <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
                  <div className="flex items-start gap-3">
                    <MdWarning className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-800 dark:text-amber-300 space-y-1">
                      <p className="font-semibold">Web Platform Limitations</p>
                      <p>OS-level screenshot tools (Windows Snipping Tool, phone power+volume) operate below the browser layer and cannot be technically blocked. Protections below make every captured image a traceable evidence artifact and deter casual leaks.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Screenshot toggle */}
                  <div className="flex items-start justify-between p-5 rounded-xl border border-[#0088cc]/20 dark:border-slate-700 bg-[#0088cc]/5 dark:bg-slate-800/50">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                        <MdScreenshotMonitor className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-[hsl(var(--foreground))] dark:text-white">Allow Screenshots</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mt-0.5">
                          When <span className="font-medium text-red-600 dark:text-red-400">OFF</span> — a persistent semi-transparent watermark (name, role, IP, timestamp, GPS) overlays every page. Any screenshot automatically identifies the leaker.
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[hsl(var(--muted-foreground))] mt-1">Print / Save-as-PDF is also blanked out when OFF.</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                      <input
                        type="checkbox"
                        checked={contentProtection.screenshotAllowed}
                        onChange={(e) => setContentProtection({ ...contentProtection, screenshotAllowed: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#0088cc]/15 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-[#0088cc]/20 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-[#0088cc]/25 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* Screen recording toggle */}
                  <div className="flex items-start justify-between p-5 rounded-xl border border-[#0088cc]/20 dark:border-slate-700 bg-[#0088cc]/5 dark:bg-slate-800/50">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
                        <MdVideocam className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-[hsl(var(--foreground))] dark:text-white">Allow Screen Recording</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mt-0.5">
                          When <span className="font-medium text-red-600 dark:text-red-400">OFF</span> — the application blurs and darkens whenever the browser window loses focus (e.g. user switches to a recording app or screen recorder). Content returns when they refocus.
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[hsl(var(--muted-foreground))] mt-1">Deters phone-to-screen recording and casual screen capture tools.</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                      <input
                        type="checkbox"
                        checked={contentProtection.screenRecordingAllowed}
                        onChange={(e) => setContentProtection({ ...contentProtection, screenRecordingAllowed: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#0088cc]/15 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-[#0088cc]/20 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-[#0088cc]/25 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* Screen share toggle */}
                  <div className="flex items-start justify-between p-5 rounded-xl border border-[#0088cc]/20 dark:border-slate-700 bg-[#0088cc]/5 dark:bg-slate-800/50">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/40">
                        <MdScreenShare className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-[hsl(var(--foreground))] dark:text-white">Allow Screen Share</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mt-0.5">
                          When <span className="font-medium text-red-600 dark:text-red-400">OFF</span> — in-browser tab sharing via the browser's built-in screen-share API (e.g. sharing a browser tab in Teams or Zoom via browser) is blocked and the attempt is recorded in the audit log.
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[hsl(var(--muted-foreground))] mt-1">Note: desktop screen-share apps operating at OS level cannot be blocked from a browser.</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                      <input
                        type="checkbox"
                        checked={contentProtection.screenShareAllowed}
                        onChange={(e) => setContentProtection({ ...contentProtection, screenShareAllowed: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#0088cc]/15 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-[#0088cc]/20 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#0088cc]/25 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-[#0088cc]/25 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Current policy summary */}
                <div className="p-4 rounded-lg border border-[#0088cc]/20 dark:border-slate-700 bg-white dark:bg-slate-900">
                  <p className="text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-3">Current Policy Summary</p>
                  <div className="grid grid-cols-3 gap-3 text-center text-xs">
                    {[
                      { label: 'Screenshots', allowed: contentProtection.screenshotAllowed },
                      { label: 'Screen Recording', allowed: contentProtection.screenRecordingAllowed },
                      { label: 'Screen Share', allowed: contentProtection.screenShareAllowed },
                    ].map((item) => (
                      <div key={item.label} className={cn(
                        'p-3 rounded-lg border',
                        item.allowed
                          ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30'
                          : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30'
                      )}>
                        {item.allowed
                          ? <MdCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
                          : <MdError className="w-5 h-5 text-red-600 dark:text-red-400 mx-auto mb-1" />
                        }
                        <p className="font-medium text-[hsl(var(--foreground))] dark:text-white">{item.label}</p>
                        <p className={item.allowed ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                          {item.allowed ? 'Allowed' : 'Blocked'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-[#0088cc]/20 dark:border-slate-700">
                  <button
                    onClick={() => saveContentProtectionMutation.mutate(contentProtection)}
                    disabled={saveContentProtectionMutation.isPending}
                    className="px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 disabled:opacity-50 text-white font-semibold rounded-lg shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 transition-all flex items-center gap-2"
                  >
                    <MdShield className="w-5 h-5" />
                    {saveContentProtectionMutation.isPending ? 'Saving...' : 'Save Content Protection Policy'}
                  </button>
                </div>
              </div>
            )}

            {/* API & Integration */}
            {activeTab === 'api' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(var(--foreground))] dark:text-white mb-1">API & Integration</h2>
                  <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">API documentation and integration information</p>
                </div>
                <div className="space-y-6">
                  <div className="p-6 rounded-lg border border-[#0088cc]/20 dark:border-slate-700 bg-[#0088cc]/5 dark:bg-slate-800/50">
                    <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4 flex items-center gap-2">
                      <MdKey className="w-5 h-5" /> API Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                          Base API URL
                        </label>
                        <div className="p-3 rounded-lg border border-[#0088cc]/25 dark:border-slate-600 bg-white dark:bg-slate-800">
                          <code className="text-sm font-mono text-[hsl(var(--foreground))] dark:text-white">
                            {API_URL}/api/v1
                          </code>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
                          Authentication
                        </label>
                        <div className="p-3 rounded-lg border border-[#0088cc]/25 dark:border-slate-600 bg-white dark:bg-slate-800">
                          <p className="text-sm text-[hsl(var(--foreground))] dark:text-[#0088cc]/45">
                            All API requests require a Bearer token in the Authorization header:
                          </p>
                          <code className="text-xs font-mono text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mt-2">
                            Authorization: Bearer {'<your-access-token>'}
                          </code>
                        </div>
                      </div>
                      <div className="pt-2">
                        <a
                          href="/api-docs"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          <MdInfo className="w-4 h-4" />
                          View API Documentation →
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
                    <div className="flex items-start gap-3">
                      <MdInfo className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="text-sm text-blue-800 dark:text-blue-300">
                        <p className="font-semibold mb-1">API Access:</p>
                        <p className="text-blue-700 dark:text-blue-400">
                          Your access token is automatically included in API requests. For programmatic access,
                          use the token from your browser's localStorage or generate a new one through the authentication endpoint.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Clean Data — customer & vendor portal wipe */}
            {activeTab === 'data-management' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[hsl(var(--foreground))] dark:text-white mb-1">Clean Data</h2>
                  <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">
                    Remove all customer portal and vendor portal data from the system
                  </p>
                </div>

                <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
                  <div className="flex items-start gap-3">
                    <MdWarning className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-900 dark:text-amber-200">
                      <p className="font-semibold mb-1">What gets removed</p>
                      <ul className="list-disc list-inside space-y-1 text-amber-800 dark:text-amber-300">
                        <li>All customer and vendor organizations, users, and roles</li>
                        <li>Onboarding records, licenses, RFQs, quotations, purchase orders</li>
                        <li>Vessels, crew, inventory, and related operational data</li>
                        <li>Invitations and session tokens for customer/vendor users</li>
                      </ul>
                      <p className="mt-3 font-semibold">What is preserved</p>
                      <ul className="list-disc list-inside space-y-1 text-amber-800 dark:text-amber-300">
                        <li>Admin portal login credentials and users</li>
                        <li>Tech portal login credentials and users</li>
                        <li>System settings and master reference data (ports, etc.)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {cleanDataStats && (
                  <div className="p-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30">
                    <p className="font-semibold text-green-800 dark:text-green-300 mb-2">Last cleanup summary</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Organizations:</span> {cleanDataStats.organizations}</div>
                      <div><span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Users:</span> {cleanDataStats.users}</div>
                      <div><span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Roles:</span> {cleanDataStats.roles}</div>
                      <div><span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Onboardings:</span> {cleanDataStats.customerOnboardings + cleanDataStats.vendorOnboardings}</div>
                    </div>
                  </div>
                )}

                <div className="p-6 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20">
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2 flex items-center gap-2">
                    <MdDeleteForever className="w-5 h-5" />
                    Danger zone
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mb-4">
                    This action is permanent and cannot be undone. Only proceed if you want a fresh start for customer and vendor portals.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowCleanDataModal(true)}
                    disabled={cleanDataMutation.isPending}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded-lg shadow-lg transition-all flex items-center gap-2"
                  >
                    <MdDeleteForever className="w-5 h-5" />
                    {cleanDataMutation.isPending ? 'Cleaning...' : 'Clean Customer & Vendor Data'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCleanDataModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="w-full max-w-md rounded-xl border border-[#0088cc]/20 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl p-6 space-y-4">
            <h3 className="text-xl font-bold text-[hsl(var(--foreground))] dark:text-white flex items-center gap-2">
              <MdWarning className="w-6 h-6 text-red-600" />
              Confirm data cleanup
            </h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">
              Type <code className="font-mono font-semibold text-red-600 dark:text-red-400">{CLEAN_DATA_CONFIRM_PHRASE}</code> below to permanently delete all customer and vendor portal data.
            </p>
            <input
              type="text"
              value={cleanDataConfirm}
              onChange={(e) => setCleanDataConfirm(e.target.value)}
              placeholder={CLEAN_DATA_CONFIRM_PHRASE}
              className="w-full px-4 py-3 rounded-lg border border-[#0088cc]/25 dark:border-slate-600 bg-white dark:bg-slate-800 text-[hsl(var(--foreground))] dark:text-white"
              autoComplete="off"
            />
            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowCleanDataModal(false);
                  setCleanDataConfirm('');
                }}
                disabled={cleanDataMutation.isPending}
                className="px-4 py-2 rounded-lg border border-[#0088cc]/25 dark:border-slate-600 text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => cleanDataMutation.mutate(cleanDataConfirm)}
                disabled={cleanDataMutation.isPending || cleanDataConfirm !== CLEAN_DATA_CONFIRM_PHRASE}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold"
              >
                {cleanDataMutation.isPending ? 'Cleaning...' : 'Confirm cleanup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
